<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import indowColdRoomImg from './images/indow-cold-room.jpeg';
	import indowInsertInstallationImg from './images/indow-insert-installation.png';
	import indowProductImg from './images/indow-product.png';
	import manInstallingIndowInsertsImg from './images/manInstallingIndowInserts.jpeg';
	import neighborhoodWightThroughIndowWindowInsertsImg from './images/neighborhoodWightThroughIndowWindowInserts.jpeg';
	import twoWindowsWithOneIndowInsertImg from './images/twoWindowsWithOneIndowInsert.jpeg';
	import womanInstallingIndowInsertImg from './images/womanInstallingIndowInsert.jpeg';
	import womanWithBlanketWarmingUpHerHandsImg from './images/womanWithBlanketWarmingUpHerHands.png';
</script>

<Section>
	<div class="flex flex-col gap-1">
		<Image
			src={indowInsertInstallationImg}
			alt="A person touching their window frames in order to check for a window
			leak."
		/>
		<p>
			<a href="/resources/blog/acrylic-vs-glass">Source</a>
		</p>
	</div>
	<p>
		Are you a homeowner who’s wondering why an entire room in your house is
		always cold? It may just be a chilling draft or a long freezing night, but
		once you’ve experienced unbearable coldness in the comfort of your own home,
		you’ll quickly realize that this is not a problem you want in the dead of
		winter.
	</p>
	<p>
		There are several reasons your room is getting colder than it should in the
		winter. Let’s take a look at the most likely reasons.
	</p>
	<h2>Common Reasons Your Room Might Be Cold</h2>
	<Image
		src={indowColdRoomImg}
		alt="A cozy living room with brick walls, and three large windows that bring
		light into the space. "
	/>
</Section>
<Section>
	<h2>How to Test Ductwork or Blocked Vents</h2>
	<p>
		Poorly installed or broken ductwork can cause you to have severely cold days
		and even colder nights. Remember, you also have to maintain your ducts so
		your room temperatures don’t go to unbearable extremes.
	</p>
	<p>
		If you’re moving into a home that needs renovations, it could also be that
		the ductwork size does not fit the new room size.
	</p>
	<p>
		are another cause of antarctic-filled nights, usually primarily felt in the
		bedroom. If you’re wondering why your bedroom is so cold, first make sure
		your vents are at peak performance. Every room should be the same
		temperature when, for instance, your fireplace is lit.
	</p>
	<p>
		A poorly functioning vent might not be the problem, though. You may have
		blocked your vents with furniture, or it may need to be cleaned. Luckily,
		these are both
		<a href="#duct">easy fixes</a>
		that can make a world of difference.
	</p>
	<div class="flex flex-col gap-2">
		<Image
			src={indowProductImg}
			alt="A white window frame with an Indow window insert. "
		/>
		<p>
			<a
				href="/resources/blog/how-to-easily-seal-windows-for-the-winter-months"
			>
				Source
			</a>
		</p>
	</div>
</Section>
<Section>
	<h2>How to Test Thermostat and Insulation</h2>
	<p>
		are another common yet easily missed cause of cold rooms in a house. You
		might be thinking you’re upping your heat or furnace, but nothing is
		happening. Or, the thermostat changes the temperature up to a point before
		giving out.
	</p>
	<p>
		However, insulation may be the culprit you’re looking for if your thermostat
		isn’t to blame. How can you tell? If your room’s walls — as well as the room
		itself — are colder than other rooms, you might have an insulation issue
	</p>
	<p>
		If your insulation is adequate, more often than not, window cracks are the
		culprits behind the ice-cold temperatures in a home. But if you’re not
		certain what the root cause is, there are a few quick tests you can do to be
		sure of a window leak. Once you determine the location of the issue, your
		<a href="#thermostat">thermostat</a>
		or
		<a href="#insulation">insulation</a>
		issues can be fixed by following a few simple steps.
	</p>
</Section>
<Section>
	<h2>How to Test for a Window Leak</h2>
	<div class="flex flex-col gap-2">
		<Image
			alt="A woman sitting in a blanket and a woolen hat in her living room,
			warming up her hands."
			src={womanWithBlanketWarmingUpHerHandsImg}
		/>
		<p>
			<a
				href="https://www.facebook.com/indowwindows/photos/7286063401419644"
				target="_blank"
				rel="external"
			>
				Source
			</a>
		</p>
	</div>
	<p>
		The first option is simply to look for visible cracks in your windows — both
		from the outside and inside of your home. Another way to detect drafts is by
		placing your hand near a window and feeling for air.
	</p>
	<p>
		You can also perform the lighter, candle, or match test — and a hairdryer
		might need to be involved. Hold the flame up to the window to see if it is
		still or gets blown to the side. If the flame pulls towards a particular
		direction, there’s your leak. If you don’t notice a pull or are unsure, take
		a hairdryer and give it to someone to use outside the window while you do
		the test. If there’s a flicker, there’s a leak.
	</p>
	<p>
		The last test can be done with the help of a thermal camera, which you can
		buy or rent from your local hardware store. This option is more costly but
		works a lot faster by exposing the source of energy leaks and poor
		insulation areas.
	</p>
	<p>
		After identifying the source of the issue, you can get started on finding
		<a href="#windowsdoors">a suitable solution</a>
		to get your home back on track.
	</p>
	<p>
		If you’ve done one or more of the above tests and find your windows have an
		opening, or if you identified one of the other reasons listed above as your
		culprit, there’s a fix. Let’s look at a few.
	</p>
</Section>
<Section id="duct">
	<h2>How to Fix a Cold Room</h2>
	<h3>Fix Your Ductwork</h3>
	<div class="flex flex-col gap-2">
		<Image
			src={neighborhoodWightThroughIndowWindowInsertsImg}
			alt="A view from a bay window with Indow inserts. The view looks towards
			the neighborhood houses. "
		/>
		<p>
			<a
				href="https://www.facebook.com/indowwindows/photos/6470653429627316"
				target="_blank"
				rel="external"
			>
				Source
			</a>
		</p>
	</div>
	<Bubble
		title="If you’re wondering why your room is so cold compared to the rest of
		your house, your ductwork may need a fix. Follow these steps to fix a cold
		room with ductwork issues:"
	>
		<ol class="marker-number marker-circle">
			<li>
				Turn off the HVAC and check the ductwork and heating vents for debris.
			</li>
			<li>
				Look for cracks in your ductwork. You may be able to fix these with an
				aerosol polymer spray or some other gap sealant.
			</li>
			<li>Look for any other physical damages and address them.</li>
			<li>
				In addition, scout out if there’s a need to rearrange your furniture so
				your vents aren’t blocked.
			</li>
			<li>
				<p class="px-0 text-left">
					And while you’re out fixing things that affect the temperature of your
					room, it might be a good idea to check your air quality as well.
				</p>
			</li>
		</ol>
	</Bubble>
</Section>
<Section id="thermostat">
	<h2>Fix Your Thermostat</h2>
	<Bubble
		title="Check the thermostat and the results it shows. Is the display
		working? Is your AC or furnace responding to the inputs from your
		thermostat? Are there differences in temperature between rooms? If you’re
		having thermostat issues, try doing the following: "
	>
		<ol class="marker-number marker-circle">
			<li>Clean your thermostat.</li>
			<li>Replace the batteries.</li>
			<li>Check your HVAC power circuits</li>
			<li>
				Troubleshoot older thermostats by opening them and pushing the metal
				strip in either direction.
			</li>
		</ol>
	</Bubble>
	<p>
		If all of these fail, it might be time to replace your thermostat once
		you’re sure it is the source of your temperature disparities.
	</p>
</Section>
<Section id="insulation">
	<h2>Fix Your Insulation</h2>

	<p>
		If your walls, ceiling, and roof are also cold, the insulation might be
		compromised and require fixing. Additionally, you might want to check the
		fireplace if you have one, as well as your chimney, for any possible damages
		to the insulation around them.
	</p>
	<p>
		Insulation issues can also prove a hassle during these pandemic times when
		most of us do more virtual calling. Poor insulation means more noise on our
		calls, and less peace and quiet to work in.
	</p>
	<p>
		Wall and ceiling insulation issues often require professional assistance to
		ensure safety and proper maintenance. However, if you’re dealing with window
		insulation issues, there are a few ways to fix this problem without having
		to hire professionals. With this being said, not all DIY solutions are the
		best.
	</p>
	<Bubble
		title="Bubble wrap or saran wrap both act as quick fixes for insulation
		problems, but they come with a few drawbacks:"
	>
		<ol class="marker-number marker-circle">
			<li>You would have to redo them every year.</li>
			<li>
				You’re likely to have window damage due to the do-it-yourself nature
			</li>
			<li>
				You risk having to repair or repaint the area, costing more money and
				time
			</li>
		</ol>
	</Bubble>
</Section>
<Section id="windowsdoors">
	<div class="flex flex-col gap-2">
		<Image
			src={womanInstallingIndowInsertImg}
			alt="A woman installing window inserts to her kitchen window."
		/>
		<p>
			<a href="https://indowwindows.com">Source</a>
		</p>
	</div>
	<h2>Fix Your Windows and Doors</h2>
	<p>
		Are there any holes in the frame or the casement of your windows? If you did
		one of the tests above and found your windows have an air leak, caulking
		might be the solution for your cold air woes.
	</p>
	<Bubble title="To utilize this temporary fix, follow these steps:">
		<ol class="marker-number marker-circle">
			<li>Clean the area you want to caulk with soap and water.</li>
			<li>
				Once dry, steadily apply the caulk at a 45-degree angle to really get
				into those cracks.
			</li>
			<li>Use a putty knife to push the caulk into cracks if it leaks out.</li>
			<li>
				Once dry, and only if you’re not sure you put enough on there, apply
				another layer of caulk for security.
			</li>
		</ol>
	</Bubble>
	<p>
		Keep in mind, caulking works best if the temperature is 45 degrees
		Fahrenheit or higher.
	</p>
	<Bubble
		title="For air leaks that lie between the window rail and the sill, you may
		try weather stripping."
	>
		<ol class="marker-number marker-circle">
			<li>Start with a clean, dry area.</li>
			<li>Measure the width of the window.</li>
			<li>Cut your weatherstripping to your measurements.</li>
			<li>Apply using the manufacturer’s instructions.</li>
		</ol>
	</Bubble>
	<p>
		There are a few other
		<a href="/resources/blog/how-to-easily-seal-windows-for-the-winter-months">
			ways to seal your windows ahead of the winter months
		</a>
		. But if your windows are old, it’s best to consider additional solutions.
	</p>
	<p>
		At Indow, we want you to have a beautiful home. One of the best ways to
		maintain your home’s beauty is to prepare it for the seasons. There are
		various things you can do to prepare for cold months, several of them
		detailed above. However, it’s important to do it the right way.
	</p>
	<p>
		Temporary solutions may work under tight time constraints. However, there is
		an idyllic, long-term solution for insulation issues and window leaks.
	</p>
</Section>
<Section>
	<h2>Inserts for a Guaranteed Cozy and Quiet Winter</h2>
	<div class="flex flex-col gap-2">
		<Image
			alt="A man installing Indow window inserts inside his home."
			src={manInstallingIndowInsertsImg}
		/>
		<p>
			<a href="/resources/blog/6-simple-ways-fix-drafty-windows">Source</a>
		</p>
	</div>
	<p>
		If your home is not insulated properly or has leaks, you may also be
		subjected to poor sleep or find yourself using only a few rooms in your
		house.
	</p>
	<p>
		Your cold room issues can be a thing of the past with our high-quality,
		sustainable window inserts. They not only provide insulation coverage, but
		they can also soundproof your home and reduce your carbon footprint.
	</p>
	<p>
		Instead of implementing a temporary fix — like bubble wrap or saran wrap —
		you could be enjoying maximum insulation thanks to
		<a href="/resources/blog/cold-air-windows">our acrylic inserts</a>
		that work better than traditional glass ones.
	</p>
	<p>
		Why not just buy new windows? Purchasing brand new windows for your home
		<a href="/resources/blog/window-insert-replacements">
			can be extremely expensive
		</a>
		. Not to mention, most warranties on newer windows don’t last very long. Plus,
		you would have to dish out even more money for professional services, which is
		what you would need in order to ensure a proper installation.
	</p>
	<p>
		And what if you want to preserve your windows? Historic homes lose part of
		their charm when you remove the original windows and replace them with
		modern options; not to mention, you may not be allowed to replace your
		historic windows.
	</p>
	<p>
		With our inserts, you can keep your gorgeous historic windows without
		suffering through the winter. Plus, you spend a lot less money and easily
		recoup the investment compared to what you would’ve spent on brand new
		windows.
	</p>
</Section>
<Section>
	<h2>Get an Estimate for Free</h2>
	<div class="flex flex-col gap-2">
		<Image
			alt="Two white windows with white blinds are shown during the daytime."
			src={twoWindowsWithOneIndowInsertImg}
		/>
		<p>
			<a
				href="https://www.facebook.com/indowwindows/photos/a.160465867312802/6915712451788076"
				target="_blank"
				rel="external"
			>
				Source
			</a>
		</p>
	</div>
	<p>
		At Indow, our window inserts offer a cost-cutting solution for every
		homeowner who has to deal with the fierce winter weather this year and in
		years to come.
	</p>
	<p>
		Want to enjoy a quieter, more comfortable home while saving money?
		<a href="/how-to/get-indow-inserts">Get a free estimate</a>
		today and find out what your personalized solution using window inserts will look
		like.
	</p>
</Section>
<Reviews />
